/**
 * Person picker — the `[ ◉ Name  🔍 ]` control of docs/08 §3.6.
 *
 * Debounced search against `GET /persons`, keyboard-reachable results, and a
 * selected state that shows who was picked rather than an opaque uuid.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { PersonSummary } from '@famtree/shared';
import { personsApi } from '../../api/client.js';
import { lifespan, personName } from './labels.js';
import ui from '../../ui/components.module.css';
import styles from './people.module.css';

export interface PersonPickerProps {
  label: string;
  value: PersonSummary | null;
  onChange: (person: PersonSummary | null) => void;
  /** Excluded from the results — you cannot be related to yourself. */
  excludeId?: string;
}

export function PersonPicker({
  label,
  value,
  onChange,
  excludeId,
}: PersonPickerProps): JSX.Element {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const [term, setTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setTerm(draft.trim()), 250);
    return () => clearTimeout(timer);
  }, [draft]);

  const results = useQuery({
    queryKey: ['person-search', term],
    queryFn: () => personsApi.list({ q: term, limit: 10 }),
    enabled: term.length >= 2 && value == null,
  });

  if (value) {
    return (
      <div className={ui.field}>
        <span className={ui.label}>{label}</span>
        <div className={styles.row}>
          <span className={styles.rowName}>{personName(value)}</span>
          <span className={ui.muted}>{lifespan(value)}</span>
          <span className={styles.spacer} />
          <button
            className={ui.buttonSecondary}
            type="button"
            onClick={() => {
              onChange(null);
              setDraft('');
            }}
          >
            {t('people.change')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={ui.field}>
      <span className={ui.label}>{label}</span>
      <input
        className={ui.input}
        type="search"
        value={draft}
        placeholder={t('people.searchPlaceholder')}
        onChange={(event) => setDraft(event.target.value)}
      />
      {results.data && (
        <ul className={styles.pickerResults}>
          {results.data.data
            .filter((person) => person.id !== excludeId)
            .map((person) => (
              <li key={person.id}>
                <button
                  className={styles.pickerResult}
                  type="button"
                  onClick={() => onChange(person)}
                >
                  {personName(person)} <span className={ui.muted}>{lifespan(person)}</span>
                </button>
              </li>
            ))}
          {results.data.data.length === 0 && (
            <li className={ui.muted} style={{ padding: 'var(--space-2) var(--space-3)' }}>
              {t('people.noMatches', { q: term })}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
